import React, { useState } from 'react';
import WaButton from '../wa/WaButton';
import {
	QUESTION_MAX_LENGTH,
	isSubmittable,
	normalizeQuestionText,
} from '../../services/songQuestionsModel';

/* AskAboutSong — the family's write affordance on the customer Songs page.
   THE FIRST CUSTOMER WRITE SURFACE IN THE PORTAL that produces content a staff
   member reads (everything before it wrote to the family's own account or a
   scoped booking flow).

   WHAT IT IS FOR (Anthony, 2026-08-12): a student practising at home hits a
   question — about one of our notes, or about the song generally — and writes it
   down so it comes up at the next lesson, "because the students don't always
   remember." It is a memory aid redeemed in the lesson, NOT a message thread,
   and it is shaped to stay that way: one field, one send, no reply affordance,
   no history of a conversation.

   INLINE, NOT A DIALOG. The composer expands in place under the song it belongs
   to, so the question is written next to the thing it is about and the anchor is
   never ambiguous. A dialog would detach the two at exactly the moment the
   parent is trying to say "this bit, here" — and on a phone it would cover the
   note they are asking about.

   ONE COMPOSER OPEN AT A TIME is enforced by the parent (it owns `openFor`),
   not here: two half-written questions on one page is a way to lose one. */

const Label = ({ children }) => (
	<span
		style={{
			fontSize: '0.72rem',
			fontWeight: 'var(--fw-bold, 700)',
			letterSpacing: '0.06em',
			textTransform: 'uppercase',
			color: 'var(--text-muted, #7b8499)',
		}}>
		{children}
	</span>
);

/* The note being asked about, quoted above the field so the parent can see they
   picked the right one. Joined in memory by the caller — never denormalized onto
   the question, which would go stale the moment a teacher edits the note. */
const NoteContext = ({ note }) => (
	<div
		style={{
			display: 'flex',
			gap: 8,
			alignItems: 'flex-start',
			padding: '8px 10px',
			marginBottom: 10,
			borderRadius: 8,
			borderLeft: '3px solid var(--cyv-teal-600, #54ada7)',
			background: 'color-mix(in srgb, var(--cyv-teal-600, #54ada7) 8%, var(--surface-card, #fff))',
		}}>
		<i
			className='fa-sharp fa-solid fa-quote-left'
			aria-hidden='true'
			style={{ fontSize: '0.7rem', marginTop: 3, color: 'var(--cyv-teal-600, #2f7d77)' }}
		/>
		<span style={{ fontSize: '0.85rem', lineHeight: 1.5, color: 'var(--text-body, #4a5468)' }}>
			{note.text}
		</span>
	</div>
);

const AskAboutSong = ({ note = null, busy = false, error = null, onCancel, onSubmit }) => {
	const [draft, setDraft] = useState('');
	const normalized = normalizeQuestionText(draft);
	const canSend = isSubmittable(draft) && !busy;
	const remaining = QUESTION_MAX_LENGTH - normalized.length;

	/* Guard the HANDLER, not just the button. A disabled attribute is a
	   suggestion: `wa-button` is a custom element whose disabled state is a
	   property set after upgrade, so before Web Awesome registers — or anywhere
	   the element does not upgrade at all — the click still lands. Without this
	   an empty question is submittable in exactly the window where the page
	   looks fine. (Found by a test, which is how it should be found: the same
	   React-18 boolean-prop trap CLAUDE.md documents for wa-* elements.) */
	const handleSubmit = () => {
		if (!canSend) return;
		onSubmit(normalized);
	};

	return (
		<div
			style={{
				padding: '12px 15px 14px',
				borderTop: '1px solid var(--cyv-line, #f0eae1)',
				background: 'var(--surface-card-warm, var(--surface-card, #fff))',
			}}>
			<div style={{ marginBottom: 8 }}>
				<Label>{note ? 'Your question about this note' : 'Your question about this song'}</Label>
			</div>

			{note && <NoteContext note={note} />}

			<textarea
				autoFocus
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				rows={3}
				// maxLength is a courtesy, not the bound. The real one is enforced in
				// firestore.rules — a client bound is a suggestion.
				maxLength={QUESTION_MAX_LENGTH + 200}
				placeholder={
					note
						? 'What would you like us to explain or work on?'
						: "Something you got stuck on while practising?"
				}
				style={{
					width: '100%',
					boxSizing: 'border-box',
					resize: 'vertical',
					minHeight: 76,
					padding: '9px 11px',
					fontSize: '0.95rem',
					fontFamily: 'inherit',
					lineHeight: 1.5,
					color: 'var(--text-body, #4a5468)',
					background: 'var(--surface-card, #fff)',
					border: '1.5px solid var(--cyv-line, #e8e8ee)',
					borderRadius: 8,
				}}
			/>

			{/* Only appears near the limit — a permanent counter on a 1000-char
			    allowance is clutter that implies a much shorter field. */}
			{remaining <= 100 && (
				<div
					style={{
						marginTop: 4,
						fontSize: '0.78rem',
						color: remaining < 0 ? 'var(--cyv-danger, #a33)' : 'var(--text-muted, #7b8499)',
					}}>
					{remaining < 0
						? `${Math.abs(remaining)} characters too long`
						: `${remaining} characters left`}
				</div>
			)}

			{error && (
				<div
					role='alert'
					style={{
						marginTop: 8,
						fontSize: '0.85rem',
						lineHeight: 1.5,
						color: 'var(--cyv-danger, #a33)',
					}}>
					{error}
				</div>
			)}

			<div style={{ display: 'flex', gap: 8, marginTop: 11 }}>
				<WaButton
					size='small'
					variant='brand'
					appearance='accent'
					disabled={!canSend}
					loading={busy}
					onClick={handleSubmit}>
					Send to my teacher
				</WaButton>
				<WaButton size='small' appearance='outlined' disabled={busy} onClick={onCancel}>
					Cancel
				</WaButton>
			</div>

			{/* Sets the expectation the feature actually meets. It is NOT a message
			    that gets a reply — it surfaces in the lesson. Saying so here is the
			    difference between a useful tool and a parent waiting for an email. */}
			<p
				style={{
					margin: '10px 0 0',
					fontSize: '0.78rem',
					lineHeight: 1.5,
					color: 'var(--text-muted, #7b8499)',
				}}>
				Your teacher will see this at the next lesson — whoever is teaching that week.
			</p>
		</div>
	);
};

export default AskAboutSong;
